import { inject, Injectable } from '@angular/core';
import { AppService } from './app.service';
import { APIConstant } from '../constants/APIConstants';
import { ObjectLiteral } from '../models/ObjectLiteral';
import { lastValueFrom } from 'rxjs';
import { LookupService } from './lookup.service';

@Injectable({
  providedIn: 'root',
})
export class AdminDashboardService {
  lookupService = inject(LookupService);
  appService = inject(AppService);

  getCards(params: {
    MinDate: string;
    MaxDate: string;
    PaymentChannel: string;
    TaxPayerCategory: string;
    MDA: string;
    PaymentGateway: string;
    EngineCode: string;
  }) {
    return this.appService.get(
      `${APIConstant.adminDashboard.getDashboardStat}`,
      params,
    );
  }
  getSuperAdminCards(params?: ObjectLiteral) {
    return lastValueFrom(
      this.appService.get(
        // `${APIConstant.adminDashboard.getSuperAdminDashboardStat}`,
        `${APIConstant.adminDashboard.getSuperAdminDashboardCardInfo}`,
        params,
      ),
    );
  }
  getExecutiveCards(params?: ObjectLiteral) {
    return lastValueFrom(
      this.appService.get(
        // `${APIConstant.adminDashboard.getSuperAdminDashboardStat}`,
        `${APIConstant.adminDashboard.getExecutiveDashboardCardInfo}`,
        params,
      ),
    );
  }
  getExecutiveMDACards(params?: ObjectLiteral) {
    return lastValueFrom(
      this.appService.get(
        // `${APIConstant.adminDashboard.getSuperAdminDashboardStat}`,
        `${APIConstant.adminDashboard.getExecutiveMDACardInfo}`,
        params,
      ),
    );
  }

  getAnalyticsData(
    params:
      | ObjectLiteral
      | {
          MinDate: string;
          MaxDate: string;
          PaymentChannel: string;
          TaxPayerCategory: string;
          MDA: string;
          PaymentGateway: string;
          EngineCode: string;
        },
  ) {
    return lastValueFrom(
      this.appService.get(
        `${APIConstant.adminDashboard.getLgaAnalytics}`,
        params,
      ),
    );
  }

  async analyticsFilterList(blackList?: string[]) {
    const promiseArrTwo = [
      this.lookupService.getPaymentChannels(),
      this.lookupService.getPaymentGateways(),
      this.lookupService.getTaxPayerCategoriesPromise(),
    ];

    if (!blackList?.includes('mda')) {
      promiseArrTwo.push(this.lookupService.getMda());
    }
    if (!blackList?.includes('expert-system')) {
      promiseArrTwo.push(this.lookupService.getEngineCodes());
    }

    const [
      channelLookup,
      paymentGatewayLookup,
      tpcLookup,
      mdaLookup,
      engineCodeLookup,
    ] = await Promise.all(promiseArrTwo);

    return {
      channelList: channelLookup,
      mdaList: mdaLookup,
      tpcList: tpcLookup,
      gatewayList: paymentGatewayLookup,
      engineCodeList: engineCodeLookup,
    };
  }

  async getAreaOfficeListing(
    params:
      | ObjectLiteral
      | {
          Month: number;
          Year: number;
        },
  ): Promise<ObjectLiteral> {
    return lastValueFrom(
      this.appService.get(
        `${APIConstant.taxAreaOfficeManagement.getAreaOfficeListing}`,
        params,
      ),
    );
  }
  async getDashboardCardsAreaOffices(
    params:
      | ObjectLiteral
      | {
          yr: number;
        },
  ): Promise<ObjectLiteral> {
    return lastValueFrom(
      this.appService.get(
        `${APIConstant.taxAreaOfficeManagement.getDashboardCardsAreaOffices}`,
        params,
      ),
    );
  }
  async getAreaOfficeRevenueTime(
    params:
      | ObjectLiteral
      | {
          yr: number;
        },
  ): Promise<ObjectLiteral> {
    return lastValueFrom(
      this.appService.get(
        `${APIConstant.taxAreaOfficeManagement.getAreaOfficeRevenueTime}`,
        params,
      ),
    );
  }
  async getTopBottom3AreaOfficeRH(
    params:
      | ObjectLiteral
      | {
          Month: number;
          Year: number;
        },
  ): Promise<ObjectLiteral> {
    return lastValueFrom(
      this.appService.get(
        `${APIConstant.taxAreaOfficeManagement.getTopBottom3AreaOfficeRH}`,
        params,
      ),
    );
  }
  async getTop3TaxOffices(): Promise<any[]> {
    return lastValueFrom(
      this.appService.get(`${APIConstant.adminDashboard.getTop3TaxOffices}`),
    );
  }

  async getBottom3TaxOffices(): Promise<any[]> {
    return lastValueFrom(
      this.appService.get(`${APIConstant.adminDashboard.getBottom3TaxOffices}`),
    );
  }

  async getTimeSeriesTSPDashboard(
    params:
      | ObjectLiteral
      | {
          yr: string;
        },
  ): Promise<any[]> {
    return lastValueFrom(
      this.appService.get(
        `${APIConstant.adminDashboard.getTimeSeriesTSPDashboard}`,
        params,
      ),
    );
  }

  async getTop3ProgressRevenueHead(
    params:
      | ObjectLiteral
      | {
          Month: number;
          Year: number;
          PaymentChannel: string;
          TaxPayerCategory: string;
          MDA: string;
          PaymentGateway: string;
          EngineCode: string;
        },
  ): Promise<any[]> {
    return lastValueFrom(
      this.appService.get(
        `${APIConstant.adminDashboard.getTop3ProgressRevenueHead}`,
        params,
      ),
    );
  }

  async getBottom3ProgressRevenueHead(
    params:
      | ObjectLiteral
      | {
          Month: number;
          Year: number;
          PaymentChannel: string;
          TaxPayerCategory: string;
          MDA: string;
          PaymentGateway: string;
          EngineCode: string;
        },
  ): Promise<any[]> {
    return lastValueFrom(
      this.appService.get(
        `${APIConstant.adminDashboard.getBottom3ProgressRevenueHead}`,
        params,
      ),
    );
  }
}
